import { LiveKitRoom } from "@livekit/components-react";
import React, { useEffect, useRef, useState } from "react";
import OnboardMeetingAnnonymousUser from "./OnboardMeetingAnnonymousUser";
import styled from "styled-components";
import { colors } from "../../Assets/styles/colors";
import { AuthContext, useAuthService } from "../../Context/AuthContext";
import {
  MeetingsContext,
  useMeetingsService,
} from "../../Context/MeetingContext";
import CallComponent from "../AudioVideoCalls/CallComponent";

import { useCallsService } from "../../Context/CallsContext";
import { checkMicrophonePermission } from "../../Helpers/RequestMichrophonePermission";
import { useGenericComponentsStateService } from "../../Context/GenericComponentsStateContext";
import { useTranslate } from '@tolgee/react';
import WebSocketObserver from "../../Helpers/websocket/WebSocketObserver";
import { useWebSocketDataService } from "../../Context/WebsocketContext";
import AccessRequestSentPage from "./AccessRequestSentPage";
import LocalStorage from "../../Helpers/LocalStorage";
import { useLinksquaredService } from "../../Context/LinksquaredContext";
import { useNotify } from "../../Helpers/Notifications";
import { openCall } from "../../Helpers/navigationHelper";
import { useNavigate } from "react-router";
import { meetingJoinErrorMessage } from "../../Helpers/meetingJoinErrorHelper";
import { ERROR_MEETING_NOT_OPEN, ERROR_MEETING_WAITING_FOR_HOST, MEETING_LIFECYCLE_STARTED } from "../../Constants/constantValues";
import useRetryWhenMeetingOpens from "../../hooks/useRetryWhenMeetingOpens";
import moment from "moment";
import PrimaryButton from "../GenericComponents/PrimaryButton";

const MEETING_APPROVAL_POLL_INTERVAL_MS = 5000;

const DeeplinkMeeting = ({
  meetingDataFromDeepLink,
  setMeetingDataFromDeepLink,
}) => {
  const {
    user,
    userRef,
    authHasCompleted,
    handleLoginAnonUser,
    handleLogoutUser,
    anonUser,
  } = useAuthService();
  const { t } = useTranslate();
  const { setDeepLink } = useLinksquaredService();
  const {
    getMeetingById,
    getMeetings,
    requestToJoinAuthenticated,
    requestToJoinUnauthenticated,
    cancelRequestToJoin,
    currentMeetingRef,
  } = useMeetingsService();
  const { getCallDetails, selectedCall } = useCallsService();

  const navigate = useNavigate();
  const { setLoading } = useGenericComponentsStateService();
  const { add_observer, remove_observer } = useWebSocketDataService();
  const { notifyError } = useNotify();
  const [meeting, setMeeting] = useState();

  const [showAccessRequestSentPage, setShowAccessRequestSentPage] =
    useState(false);

  const [isOnboardingMeetingOpen, setIsOnboardingMeetingOpen] = useState(false);
  const [joinErrorMessage, setJoinErrorMessage] = useState("");
  const approvalHandledRef = useRef(false);
  const [waitingForHost, setWaitingForHost] = useState(false);
  const [opensAt, setOpensAt] = useRetryWhenMeetingOpens(() => joinMeeting(currentMeetingRef.current.call));

  const handleMeetingWsResponseRef = useRef(null);

  const MeetingChannelObserverRef = useRef(null);
  if (!MeetingChannelObserverRef.current) {
    MeetingChannelObserverRef.current = new WebSocketObserver(
      "MeetingChannel",
      (message) => {
        handleMeetingWsResponseRef.current(message);
      }
    );
  }
  const MeetingChannelObserver = MeetingChannelObserverRef.current;

  const handleMeetingWsResponse = (message) => {
    let response = message;
    const currentMeeting = currentMeetingRef.current;

    if (typeof response === "string") {
      response = JSON.parse(message);
    }

    if (waitingForHost && response.status === MEETING_LIFECYCLE_STARTED && response.meeting_id === meetingDataFromDeepLink.id) {
      setWaitingForHost(false);
      joinMeeting(currentMeetingRef.current.call);
      return;
    }

    const isMessageForMe = checkIsMessageForMe(currentMeeting, message);

    if (response.meeting_id === meetingDataFromDeepLink.id && isMessageForMe) {
      if (response.status === "rejected") {
        notifyError(t('meeting_onboard.request_rejected'));
        handleCloseMeeting();
      }

      if (response.status === "approved") {
        getMeetingById(
          meetingDataFromDeepLink.id,
          (response) => {
            const meeting = response.data.meeting;
            if (approvalHandledRef.current) {
              return;
            }
            approvalHandledRef.current = true;
            currentMeetingRef.current = meeting;
            setShowAccessRequestSentPage(false);
            joinMeeting(meeting.call);
            setMeeting(meeting);
          },
          (error) => {
            currentMeetingRef.current = null;
            handleCloseMeeting();
            notifyError("Can not fetch this meeting");
          }
        );
      }
    }
  };

  const checkIsMessageForMe = (currentMeeting, message) => {
    const currentUser = userRef.current;

    let found = currentMeeting?.participants?.find(
      (participant) => participant.user.id === currentUser.id
    );

    if (found) {
      const participantID = message.participant_id;
      return found.id === participantID;
    } else {
      return false;
    }
  };

  const loadMeetings = () => {
    getMeetings(
      {},
      (response) => { },
      (error) => {
        console.log(error);
      }
    );
  };

  const handleGetMeetingById = (meetingId) => {
    return new Promise((resolve, reject) => {
      getMeetingById(
        meetingId,
        (response) => {
          const meeting = response.data.meeting;
          currentMeetingRef.current = meeting;
          setMeeting(meeting);

          if (meeting.call) {
            resolve(true);
          } else {
            resolve(false);
          }
        },
        (error) => {
          currentMeetingRef.current = null;
          setMeeting(null);
          resolve(false);
        }
      );
    });
  };

  const hasAccessToMeeting = async (meetingId) => {
    return await handleGetMeetingById(meetingId);
  };

  const joinMeeting = (call) => {
    const isElectron = !!window.electron;

    setLoading(true);
    getCallDetails(
      call.uuid,
      (response) => {
        const uuid = response.data.details.uuid;
        setLoading(false);

        if (uuid) {
          openCall(
            navigate,
            response.data.details.uuid,
            currentMeetingRef.current.id,
            anonUser.current
          );
          setMeetingDataFromDeepLink(null);
        }

        if (isElectron && !anonUser.current) {
          handleCloseMeeting();
        }
      },
      (error) => {
        setLoading(false);
        if (error?.response?.data?.error_code === ERROR_MEETING_NOT_OPEN) {
          setOpensAt(error.response.data.opens_at);
          return;
        }
        if (error?.response?.data?.error_code === ERROR_MEETING_WAITING_FOR_HOST) {
          remove_observer(MeetingChannelObserver);
          add_observer(MeetingChannelObserver);
          setWaitingForHost(true);
          return;
        }
        setJoinErrorMessage(meetingJoinErrorMessage(error, t));
      }
    );
  };

  const requestAccessToMeetingAuthenticated = (meetingId) => {
    requestToJoinAuthenticated(
      meetingId,
      (response) => {
        const meeting = response.data.meeting;

        if (!currentMeetingRef.current && !meeting) {
          notifyError(t('general.something_wrong'));
          return;
        }

        if (meeting) {
          currentMeetingRef.current = meeting;
        }

        setMeeting(currentMeetingRef.current);
        setShowAccessRequestSentPage(true);
      },
      (error) => {
        notifyError(t('general.something_wrong'));
      }
    );
  };

  const cancelRequestToJoinMeeting = (meetingId) => {
    return new Promise((resolve) => {
      cancelRequestToJoin(
        meetingId,
        (response) => {
          resolve(true); // Return true on success
        },
        (error) => {
          resolve(false); // Return false on failure
        }
      );
    });
  };

  const requestAccessToMeetingUnauthenticated = (
    meetingId,
    name,
    callLanguage,
    language
  ) => {
    return new Promise((resolve) => {
      requestToJoinUnauthenticated(
        meetingId,
        name,
        callLanguage,
        language,
        (response) => {
          handleLoginAnonUser(response.data.user);
          LocalStorage.setAuthenthicationToken(response.data.token);
          LocalStorage.setRefreshToken(response.data.refresh_token);
          // This is needed as otherwise the user won't refresh its token
          LocalStorage.setSSOFlag(true);
          resolve(true); // Return true on success
        },
        (error) => {
          notifyError(t('general.something_wrong'));
          resolve(false); // Return false on failure
        }
      );
    });
  };

  const handleJoinUnauthenthicated = async (
    meetingId,
    name,
    callLanguage,
    language
  ) => {
    setLoading(true);
    try {
      const canJoin = await requestAccessToMeetingUnauthenticated(
        meetingId,
        name,
        callLanguage,
        language
      );
      setIsOnboardingMeetingOpen(false);
      setShowAccessRequestSentPage(true);

      if (canJoin) {
        setLoading(false);
        const hasAccess = await hasAccessToMeeting(meetingDataFromDeepLink.id);

        if (hasAccess) {
          checkMicrophonePermission().then((permission) => {
            if (permission === "granted") {
              joinMeeting(currentMeetingRef.current.call);
            } else if (permission === "denied") {
              notifyError(
                t('call_modal.enable_calling_message')
              );
            }
          });
        } else {
          add_observer(MeetingChannelObserver);

          if (currentMeetingRef.current) {
            const status = checkMyStatus(currentMeetingRef.current);

            if (status === "rejected") {
              notifyError(t('meeting_onboard.request_rejected'));
              setMeetingDataFromDeepLink(null);

              return;
            }
          }

          requestAccessToMeetingAuthenticated(meetingDataFromDeepLink.id);
        }
      }
    } catch (error) {
      notifyError(t('general.something_wrong'));
      handleCloseMeeting();
    }
  };

  const checkMyStatus = (meeting) => {
    if (!meeting) {
      return;
    }
    let found = meeting.participants.find(
      (participant) => participant.user.id === userRef.current.id
    );
    if (found) {
      return found.status;
    } else {
      return null;
    }
  };

  const handleCloseMeeting = async () => {
    setShowAccessRequestSentPage(false);
    setWaitingForHost(false);
    setOpensAt(null);
    setMeetingDataFromDeepLink(null);
    setIsOnboardingMeetingOpen(false);
    await handleMeetingHasEnded();
  };

  useEffect(() => {
    loadMeetings();
  }, []);

  const checkAccessWithUser = async () => {
    const hasAccess = await hasAccessToMeeting(meetingDataFromDeepLink.id);

    if (user && hasAccess) {
      checkMicrophonePermission().then((permission) => {
        if (permission === "granted") {
          joinMeeting(currentMeetingRef.current.call);
        } else if (permission === "denied") {
          notifyError(t('call_modal.enable_calling_message'));
        }
      });
    }

    if (user && !hasAccess) {
      add_observer(MeetingChannelObserver);
      if (currentMeetingRef.current) {
        const status = checkMyStatus(currentMeetingRef.current);
        if (status === "rejected") {
          notifyError(t('meeting_onboard.request_rejected'));
          setMeetingDataFromDeepLink(null);
        } else {
          requestAccessToMeetingAuthenticated(meetingDataFromDeepLink.id);
        }
      } else {
        requestAccessToMeetingAuthenticated(meetingDataFromDeepLink.id);
      }
    }
  };

  const checkAccessNoUser = () => {
    setIsOnboardingMeetingOpen(true);
  };

  const handleMeetingHasEnded = async () => {
    setMeetingDataFromDeepLink(null);
    setDeepLink("");

    if (currentMeetingRef.current) {
      await cancelRequestToJoinMeeting(currentMeetingRef.current.id);
    }

    currentMeetingRef.current = null;

    if (anonUser.current) {
      handleLogoutUser();
    }
  };

  useEffect(() => {
    handleMeetingWsResponseRef.current = handleMeetingWsResponse;
  });

  useEffect(() => {
    if (!showAccessRequestSentPage || !meetingDataFromDeepLink) {
      return;
    }
    approvalHandledRef.current = false;
    const meetingId = meetingDataFromDeepLink.id;
    const interval = setInterval(() => {
      getMeetingById(
        meetingId,
        (response) => {
          const polledMeeting = response.data.meeting;
          if (approvalHandledRef.current) {
            return;
          }
          if (polledMeeting?.call) {
            approvalHandledRef.current = true;
            clearInterval(interval);
            currentMeetingRef.current = polledMeeting;
            setShowAccessRequestSentPage(false);
            setMeeting(polledMeeting);
            joinMeeting(polledMeeting.call);
          } else if (checkMyStatus(polledMeeting) === "rejected") {
            approvalHandledRef.current = true;
            clearInterval(interval);
            notifyError(t('meeting_onboard.request_rejected'));
            handleCloseMeeting();
          }
        },
        () => {}
      );
    }, MEETING_APPROVAL_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [showAccessRequestSentPage, meetingDataFromDeepLink]);

  useEffect(() => {
    if (!authHasCompleted) {
      return;
    }

    setJoinErrorMessage("");

    if (!user) {
      checkAccessNoUser();
    } else {
      checkAccessWithUser();
    }
  }, [meetingDataFromDeepLink, authHasCompleted]);

  useEffect(() => {
    return () => {
      remove_observer(MeetingChannelObserver);
    };
  }, []);

  if (!meetingDataFromDeepLink) {
    return;
  }

  return (
    <FullscreenWrapper>
      {showAccessRequestSentPage && (
        <AccessRequestSentPage
          meetingTitle={meetingDataFromDeepLink.title}
          closeMeeting={handleCloseMeeting}
        />
      )}

      {opensAt && (
        <AccessRequestSentPage
          title={t('meeting_onboard.meeting_not_open_title')}
          subtitle={t('meeting_onboard.meeting_not_open_subtitle', { time: moment(opensAt).format("LLL") })}
          closeMeeting={handleCloseMeeting}
        />
      )}

      {waitingForHost && (
        <AccessRequestSentPage
          title={t('meeting_onboard.waiting_for_host_title')}
          subtitle={t('meeting_onboard.waiting_for_host_subtitle')}
          closeMeeting={handleCloseMeeting}
        />
      )}

      {isOnboardingMeetingOpen && (
        <OnboardMeetingAnnonymousUser
          meeting={meetingDataFromDeepLink}
          requestToJoin={handleJoinUnauthenthicated}
          handleMeetingHasEnded={handleMeetingHasEnded}
        />
      )}

      {joinErrorMessage && (
        <JoinErrorContainer>
          <JoinErrorCard>
            <JoinErrorText>{joinErrorMessage}</JoinErrorText>
            <PrimaryButton
              onClick={() => {
                setJoinErrorMessage("");
                handleCloseMeeting();
              }}
              style={{ padding: "12px 16px" }}
            >
              {t("general.ok")}
            </PrimaryButton>
          </JoinErrorCard>
        </JoinErrorContainer>
      )}
    </FullscreenWrapper>
  );
};

export default DeeplinkMeeting;

const FullscreenWrapper = styled.div`
  position: absolute;
  width: 100%;
  z-index: 10;
  background: ${({ theme }) => theme.menu.background};
  height: 100%;
  display: flex;
`;

const JoinErrorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 16px;
`;

const JoinErrorCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: ${({ theme }) => theme.menu.groupContentBackground};
  border-radius: 8px;
  padding: 20px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
  text-align: center;
`;

const JoinErrorText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 14px;
  line-height: 20px;
`;
